import React, { useCallback, useState } from 'react';
import { Info } from 'lucide-react';
import { KEEP_PENDING, useAsyncAction } from '../hooks/useAsyncAction';
import { cn } from '../lib/utils';
import { ThemeToggle } from './ui/theme-toggle';
import { Alert, AlertDescription } from './ui/alert';
import { LandingBackground } from './landing/LandingBackground';
import { LoginPanel } from './landing/LoginPanel';
import { useRateLimit } from '../context/RateLimitContext';
import { readRateLimitError } from '../utils/rateLimit';
import { persistRateLimitFromError } from '../utils/rateLimitStorage';
import { consumeAuthRedirectReason } from '../utils/authRedirect';
import { buildApiUrl } from '../utils/apiBaseUrl';
import FooterLegalNav from './layout/FooterLegalNav';
import { getFooterMeta } from '../utils/footerMeta';

function HeroHeadline({ className }) {
  return (
    <div className={cn('landing-enter shrink-0 animate-fade-in-up', className)}>
      <h1 className="mx-auto max-w-lg text-[clamp(1.25rem,2.8vw,2.25rem)] font-semibold leading-[1.15] tracking-tight text-foreground">
        Every hour{' '}
        <span className="bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600 bg-clip-text text-transparent dark:from-blue-300 dark:via-blue-400 dark:to-blue-500">
          accounted
        </span>{' '}
        for.
      </h1>
      <p className="landing-hide-when-short mx-auto mt-2 max-w-lg text-xs text-muted-foreground sm:mt-2.5 sm:text-sm">
        Log project time, submit for approval, and give leaders the visibility they need.
      </p>
    </div>
  );
}

const LandingPage = () => {
  const { year } = getFooterMeta();
  const { block: rateLimitBlock } = useRateLimit();
  const [loginError, setLoginError] = useState(null);
  const [showSessionExpiredNotice] = useState(
    () => consumeAuthRedirectReason() === 'session_expired'
  );

  const { execute: handleMicrosoftLogin, isPending: isLoading } = useAsyncAction(
    useCallback(async () => {
      setLoginError(null);

      try {
        const loginUrl = buildApiUrl('/api/auth/login');
        const response = await fetch(loginUrl, {
          method: 'GET',
          credentials: 'include',
          redirect: 'manual',
          headers: { Accept: 'application/json' },
        });

        if (response.status === 429) {
          const rateLimitError = await readRateLimitError(response);
          persistRateLimitFromError(rateLimitError);
          setLoginError(null);
          return;
        }

        if (response.status === 302 || response.status === 0 || response.type === 'opaqueredirect') {
          const location = response.headers.get('Location');
          window.location.href = location || loginUrl;
          return KEEP_PENDING;
        }

        if (!response.ok) {
          setLoginError('Unable to start sign-in right now. Please try again in a moment.');
          return;
        }

        window.location.href = loginUrl;
        return KEEP_PENDING;
      } catch {
        setLoginError('Unable to connect to the sign-in service. Please try again.');
      }
    }, [])
  );

  return (
    <div className="landing-page-shell relative flex flex-col overflow-y-auto bg-background">
      <LandingBackground variant="light" />

      <header className="relative z-50 flex-shrink-0 border-b border-border/50 bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex h-[var(--header-h)] max-w-[90rem] min-w-0 items-center justify-between gap-2 px-3 sm:gap-3 sm:px-6 lg:px-8">
          <img
            src="/KL image.png"
            alt="Kadel Labs"
            className="h-8 flex-shrink-0 object-contain sm:h-9 laptop:h-10"
          />
          <ThemeToggle />
        </div>
      </header>

      <main
        id="main-content"
        tabIndex={-1}
        className="relative z-10 mx-auto flex min-h-0 min-w-0 w-full max-w-[90rem] flex-1 flex-col items-center justify-center px-4 py-8 sm:px-6 sm:py-10 lg:px-8"
      >
        <div className="relative z-10 mx-auto w-full min-w-0 max-w-lg shrink-0">
          <HeroHeadline className="mb-6 px-4 py-4 text-center sm:mb-8 [animation-delay:200ms]" />
          {showSessionExpiredNotice ? (
            <Alert
              className="mb-4 border-blue-200/70 bg-blue-50/90 text-blue-900 dark:border-blue-800/50 dark:bg-blue-950/35 dark:text-blue-100"
            >
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" aria-hidden="true" />
              <AlertDescription>
                Your session expired. Please sign in again.
              </AlertDescription>
            </Alert>
          ) : null}
          <LoginPanel
            onSignIn={handleMicrosoftLogin}
            isLoading={isLoading}
            errorMessage={loginError}
            rateLimitBlock={rateLimitBlock}
            className="mx-auto"
          />
        </div>
      </main>

      <footer className="relative z-10 flex min-h-[var(--landing-footer-h)] flex-shrink-0 items-center justify-center border-t border-border/40 bg-background/60 px-2 py-0 text-muted-foreground backdrop-blur-sm sm:px-6 sm:py-0">
        <div className="flex flex-wrap items-center justify-center gap-x-1 text-ds-caption leading-tight">
          <span className="inline-flex min-h-[44px] items-center px-1 max-lg:min-h-[44px] lg:min-h-8 lg:px-1.5">
            © {year} Kadel Labs
          </span>
          <span className="select-none px-1.5 text-gray-400 dark:text-gray-500 max-lg:px-2" aria-hidden="true">
            ·
          </span>
          <FooterLegalNav
            linkClassName="text-muted-foreground transition-colors hover:text-foreground"
          />
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
